import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore';
import { FaBell, FaTimes, FaExclamationTriangle, FaCheckCircle, FaCalendarCheck } from 'react-icons/fa';

function Notifications() {
    const [projectNotifications, setProjectNotifications] = useState([]);
    const [bookingNotifications, setBookingNotifications] = useState([]);
    const [readNotifications, setReadNotifications] = useState(() => {
        // Al iniciar, leemos del localStorage las notificaciones ya leídas
        const saved = localStorage.getItem('readNotifications');
        return saved ? JSON.parse(saved) : [];
    });
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- CONFIGURACIÓN DE AUDIO ---
    // Usamos una referencia para el audio y un flag para evitar que suene al cargar la web
    const audioRef = useRef(new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3'));
    const isInitialLoad = useRef(true);

    // Guardamos en localStorage cada vez que la lista de leídas cambia
    useEffect(() => {
        localStorage.setItem('readNotifications', JSON.stringify(readNotifications));
    }, [readNotifications]);

    // --- 1. LÓGICA EXISTENTE: Proyectos en Tránsito ---
    useEffect(() => {
        const q = query(
            collection(db, 'proyectosEnTransito'),
            where('estado', '!=', 'Completado')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setError(null);
            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            const limite = new Date();
            limite.setDate(hoy.getDate() + 7);

            const newNotifications = [];

            snapshot.docs.forEach(doc => {
                const data = doc.data();
                const fechaValue = data.fechaEntrega;
                if (!fechaValue) return;

                let fechaEntrega;
                let fechaFormateada;

                if (typeof fechaValue.toDate === 'function') {
                    fechaEntrega = fechaValue.toDate();
                    fechaFormateada = fechaEntrega.toLocaleDateString('es-ES');
                } else if (typeof fechaValue === 'string') {
                    const parts = fechaValue.split('/');
                    if (parts.length !== 3) return;
                    fechaEntrega = new Date(parts[2], parts[1] - 1, parts[0]);
                    fechaFormateada = fechaValue;
                } else {
                    return;
                }
                
                fechaEntrega.setHours(0,0,0,0);

                if (fechaEntrega >= hoy && fechaEntrega <= limite) {
                    const diasRestantes = Math.round((fechaEntrega - hoy) / (1000 * 60 * 60 * 24));
                    newNotifications.push({
                        id: doc.id,
                        tipo: 'PROYECTO',
                        titulo: `¡Atención! Proyecto '${data.nombreProyecto}'`,
                        mensaje: diasRestantes === 0
                            ? `La fecha de entrega es HOY.`
                            : `La fecha de entrega es en ${diasRestantes} día(s). (${fechaFormateada})`,
                    });
                }
            });

            setProjectNotifications(newNotifications);
            setLoading(false);
        }, (err) => {
            console.error("Error en proyectos:", err);
            setError("Error al cargar notificaciones.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // --- 2. NUEVA LÓGICA: Notificación de Citas + EFECTO DE SONIDO ---
    useEffect(() => {
        const qCitas = query(
            collection(db, 'citas'),
            orderBy('creadoEn', 'desc'),
            limit(10)
        );

        const unsubscribeCitas = onSnapshot(qCitas, (snapshot) => {
            // Detectar si hay documentos nuevos añadidos después de la carga inicial
            const hasNewDocs = snapshot.docChanges().some(change => change.type === 'added');

            // Si no es la carga inicial y hay una cita nueva, ¡SUENA!
            if (!isInitialLoad.current && hasNewDocs) {
                audioRef.current.play().catch(error => {
                    console.log("El navegador bloqueó el audio hasta que interactúes con la página:", error);
                });
            }

            const newBookings = snapshot.docs.map(doc => {
                const data = doc.data();
                const fechaCita = data.fechaInicio?.toDate().toLocaleString('es-ES', { 
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                });

                return {
                    id: doc.id,
                    tipo: 'CITA',
                    titulo: `Nueva Cita: ${data.nombreCliente}`,
                    mensaje: `Agendada para el ${fechaCita}. (${data.origen})`,
                    creadoEn: data.creadoEn
                };
            });

            setBookingNotifications(newBookings);
            // Marcamos que la carga inicial ya terminó
            isInitialLoad.current = false;
        });

        return () => unsubscribeCitas();
    }, []);

    const handleMarkAsRead = (notificationId) => {
        if (!readNotifications.includes(notificationId)) {
            setReadNotifications([...readNotifications, notificationId]);
        }
    };
    
    // Unificar y filtrar por no leídas
    const allNotifications = [...projectNotifications, ...bookingNotifications];
    const unreadNotifications = allNotifications.filter(
        notif => !readNotifications.includes(notif.id)
    );

    const unreadCount = unreadNotifications.length;

    return (
        <div className="relative">
            {/* Icono de la campana */}
            <button onClick={() => setIsOpen(!isOpen)} className="relative text-gray-600 hover:text-gray-800 focus:outline-none transition-transform active:scale-90">
                <FaBell size={24} />
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 block h-5 w-5 -mt-1 -mr-1">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping"></span>
                        <span className="relative inline-flex rounded-full h-5 w-5 bg-red-600 items-center justify-center text-white text-[10px] font-bold">
                            {unreadCount}
                        </span>
                    </span>
                )}
            </button>

            {/* Panel desplegable */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-85 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-fade-in">
                    <div className="p-4 flex justify-between items-center bg-gray-50 border-b">
                        <h3 className="font-bold text-gray-800 flex items-center gap-2">
                            <FaBell className="text-teal-500" /> Notificaciones
                        </h3>
                        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600">
                            <FaTimes />
                        </button>
                    </div>
                    
                    <div className="py-2 max-h-[450px] overflow-y-auto custom-scrollbar">
                        {loading && <p className="text-center text-gray-400 py-6 text-sm">Sincronizando con el GRID...</p>}
                        {error && <p className="text-center text-red-500 font-semibold py-4">{error}</p>}
                        
                        {!loading && !error && unreadCount === 0 && (
                            <div className="text-center py-10">
                                <FaCheckCircle className="mx-auto text-gray-200 mb-2" size={40} />
                                <p className="text-gray-400 text-sm">Todo al día, patrón.</p>
                            </div>
                        )}

                        {!loading && !error && unreadNotifications.map(notif => (
                            <div key={notif.id} className="flex items-start px-4 py-4 border-b hover:bg-teal-50 group transition-colors relative">
                                <div className="mt-1 mr-3 flex-shrink-0">
                                    {notif.tipo === 'CITA' ? (
                                        <FaCalendarCheck className="text-teal-500" size={18} />
                                    ) : (
                                        <FaExclamationTriangle className="text-yellow-500" size={18} />
                                    )}
                                </div>
                                <div className="flex-grow pr-6">
                                    <p className="font-bold text-sm text-gray-800 leading-tight">{notif.titulo}</p>
                                    <p className="text-xs text-gray-600 mt-1">{notif.mensaje}</p>
                                </div>
                                <button 
                                    onClick={() => handleMarkAsRead(notif.id)} 
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-300 hover:text-teal-600 transition-colors"
                                    title="Marcar como leída"
                                >
                                    <FaCheckCircle size={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                    
                    {unreadCount > 0 && (
                        <div className="p-3 bg-gray-50 text-center">
                            <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">HoppyTime Intelligence System</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default Notifications;